/* Service placeholder pages — dark midnight theme, consistent with the site.
   Route: /services/:slug */
import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const SERVICES = {
  "custom-software": {
    title: "Custom Software Development",
    desc: "Bespoke internal tools, dashboards, and workflow systems engineered around how your business actually operates.",
  },
  "ai-software": {
    title: "AI Software Solutions",
    desc: "Production-grade AI integrations — from intelligent document processing to predictive analytics — wired into your existing stack.",
  },
  "crm-development": {
    title: "CRM Development",
    desc: "Custom CRM systems and integrations that keep your pipeline organized and your sales team moving.",
  },
  "business-automation": {
    title: "Business Automation",
    desc: "Automate repetitive operations — follow-ups, reporting, data entry — and reclaim hours every single week.",
  },
};

const ServicePlaceholderPage = () => {
  const { slug } = useParams();
  const service = SERVICES[slug] || {
    title: "Service",
    desc: "A dedicated Digital Master Media LLC service offering.",
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-['Inter',-apple-system,sans-serif] antialiased">
      <Header />

      <section className="relative overflow-hidden pt-32 pb-24 sm:pt-40">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-black" />
          <motion.div
            className="absolute -top-32 right-[-8rem] w-[28rem] h-[28rem] rounded-full bg-blue-600/20 blur-3xl"
            animate={{ x: [0, -40, 0], y: [0, 50, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            Our Services
          </p>
          <h1
            className="mt-4 font-['Poppins'] text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08]"
            data-testid="service-page-title"
          >
            {service.title}
          </h1>
          <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-2xl">
            {service.desc}
          </p>

          <div className="mt-8 rounded-2xl border border-slate-700/60 bg-slate-800/40 backdrop-blur-xl p-6 sm:p-8 max-w-2xl">
            <p className="text-sm text-slate-300 leading-relaxed">
              This service page is currently being finalized. Our team is ready to
              scope your project today — tell us what you need and we'll come back
              with a plan, timeline, and fixed quote within 24 hours.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="service-page-contact-cta"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold px-7 py-3.5 rounded-xl shadow-lg shadow-blue-600/25 hover:-translate-y-0.5 transition-all duration-200"
              >
                Request a Free Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/website-development"
                data-testid="service-page-webdev-cta"
                className="inline-flex items-center justify-center gap-2 border border-slate-600 text-slate-200 hover:border-blue-500 hover:text-white hover:bg-blue-600/10 font-semibold px-7 py-3.5 rounded-xl transition-all duration-200"
              >
                <PhoneCall className="w-4 h-4" />
                See Website Packages
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePlaceholderPage;
